/* eslint-disable react-hooks/exhaustive-deps */

import React, {
    ComponentProps,
    FC,
    MouseEvent,
    useContext,
    useEffect,
    useState
} from 'react';
import dynamic from 'next/dynamic';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { ApolloError } from '@apollo/client';
import {
    Breadcrumbs,
    PaginationOnPageChangeEvent,
    useModal
} from '@blueprint/ui';
import triggerTracking from '@blueprint/tracking';
import {
    getOptimizelyFeatureFlag,
    selectForcedFeatures,
    withOptimizely,
    WithOptimizelyProps
} from 'shared/optimizely';

import {
    filtersValuesSelector,
    identifierSelector
} from '../../reducers/filters';
import {
    resetResultsAsync,
    selectPageNumber,
    totalPagesSelector
} from '../../reducers/searchResults';
import { selectCurrentSortOrder } from '../../reducers/listingsSortOrder';
import Head from 'shared/components/Head/Head';
import AuthModal from 'shared/components/AuthModal/AuthModal';
import { Listings } from '../Listings';
import { MOBILE_BREAKPOINT } from 'listing-details/constants';

import { SearchResultsHeader } from './SearchResultsHeader';
import { DiscoverMore } from '../banners/DiscoverMore';
import {
    PaginationContainer,
    SearchResultsLayoutGroup,
    StyledPagination
} from '../Layout/Layout.styles';
import {
    Links,
    ProductTrackingObject,
    SearchContext,
    SearchType,
    SeoBlurb,
    ServerData
} from '../SearchContext';
import { Listing } from '../Listings/Listings.types';
import {
    buildCanonicalPath,
    buildQueryParamsString,
    segmentSearchEvent,
    updateResults
} from '../helpers';
import { activateMobileBanner } from '../helpers/activateMobileBanner';
import { getProductImpression } from '../FeaturedPropertyList/featuredPropertyTracking';
import { PropertyOrPromoItem } from '../helpers/featuredProperties';
import useIsWidthLessThanBreakpoint from 'shared/hooks/app/useIsWidthLessThanBreakpoint';
import { useIsLoggedIn } from 'shared/hooks/useIsLoggedIn';
import { removeEverythingAfterHash, trackPage } from '../../shared/helpers';
import { AdvertSlotDfp } from 'shared/components/Adverts/AdvertSlotDfp';
import FeaturedPropertyList from '../FeaturedPropertyList';
import { identifyUser } from 'shared/helpers/segment/identifyUser';
import { getJSONLD } from '../Seo/schemaMarkup';
import { shouldIndexPath } from '../Seo/noFollow';
import { BreadcrumbsShowMobileWrapper } from './SearchResults.styles';
import { SortOrderDropdown } from './SearchResultsHeader/SortOrderDropdown';
import { breakpoints } from '../common/constants';
import { AD_UNIT_ROOTS } from 'shared/components/Adverts/types';
import { getPageSectionForAdUnit } from '../../shared/components/Adverts/lib';
import { PAGE_MAPPING } from 'shared/components/Adverts/constants';
import { BaseTaxonomy } from 'shared/tracking/contracts';
import AuthPrompts from 'shared/components/AuthPrompts/AuthPrompts';
import { AuthModalMode } from 'shared/components/AuthModal/AuthModal.types';
import { updatePathPageNumber } from 'search/helpers/updatePathPageNumber';
import { useSaveListing } from 'search-map-view/components/AuthActions/hooks/useSaveListing';

const EmailAgents = dynamic<
    ComponentProps<typeof import('../EmailAgents/EmailAgents').EmailAgents>
>(
    () =>
        import('../EmailAgents/EmailAgents').then(
            (module) => module.EmailAgents
        ),
    {
        ssr: false
    }
);

export type SearchResultProps = {
    regularListings: Listing[];
    extendedListings: Listing[];
    featuredProperties: PropertyOrPromoItem[];
    canonicalPath: string;
    searchResults: ServerData['searchResults'];
    discoverMore: ServerData['discoverMore'];
    breadcrumbs: ServerData['breadcrumbs'];
    loading: boolean;
    transactionType: SeoBlurb['transactionType'];
    error?: ApolloError;
    links: Links;
};

const BottomPageAdWrapper = styled.div(
    ({ theme }) => css`
        padding-top: ${theme.spacing['16px']};
    `
);

const SearchResults: FC<SearchResultProps & WithOptimizelyProps> = ({
    regularListings,
    extendedListings,
    featuredProperties,
    canonicalPath: canonicalPathFromServer,
    searchResults,
    breadcrumbs,
    discoverMore,
    loading,
    transactionType,
    links,
    error,
    optimizely
}) => {
    const reduxDispatch = useDispatch();
    const router = useRouter();
    const { query, asPath } = router;
    const path = removeEverythingAfterHash(asPath);

    const { state, dispatch } = useContext(SearchContext);

    const {
        searchType,
        currentSearchSource,
        userAlertId,
        isEditSavedSearchBannerVisible,
        previousSavedSearchUri,
        savedSearchAndAlerts,
        favourites,
        actionAfterAuth,
        seoAccordions
    } = state;

    const isLoggedIn = useIsLoggedIn();

    const filters = useSelector(filtersValuesSelector);
    const currentSortOrder = useSelector(selectCurrentSortOrder);
    const { selectedValue: identifierSelectedValue } = useSelector(
        identifierSelector
    );

    const totalPages = useSelector(totalPagesSelector);
    const pageNumber = useSelector(selectPageNumber);

    const [canonicalPath, setCanonicalPath] = useState(canonicalPathFromServer);
    const [isWidthLessThanBreakpoint] = useIsWidthLessThanBreakpoint();
    const [isMobile] = useIsWidthLessThanBreakpoint([MOBILE_BREAKPOINT]);

    const [internalPromoParam, setInternalPromoParam] = useState('');

    const [isAuthModalShowing, openAuthModal, closeAuthModal] = useModal();
    const [authMode, setAuthMode] = useState<AuthModalMode>('register');
    const [authHeader, setAuthHeader] = useState<string | undefined>(undefined);

    const featureFlagOverrides = useSelector(selectForcedFeatures);
    const isMobileBannerEnabled = getOptimizelyFeatureFlag(
        'mobile_app_banner',
        featureFlagOverrides,
        optimizely
    );

    const authenticateSaveToggle = useSaveListing(
        dispatch,
        favourites.listings,
        isLoggedIn,
        searchResults?.analyticsTaxonomy,
        setAuthHeader,
        openAuthModal,
        'search results'
    );

    useEffect(() => {
        const isLSRP =
            searchType === SearchType.ForSale ||
            searchType === SearchType.ToRent;
        // We're only testing on searches with no region property for now
        // as they have a much lower propensity to click email-all
        const hasRegion = !!searchResults?.analyticsTaxonomy?.regionName;
        if (isLSRP && !hasRegion) {
            activateMobileBanner(isMobileBannerEnabled, isMobile);
        }
    }, [isMobile]);

    const resetActionAfterAuth = async () => {
        dispatch({
            type: 'SET_ACTION_AFTER_AUTH',
            payload: {
                action: () => null,
                eventLabel: 'save_listing'
            }
        });
    };

    useEffect(() => {
        const promoParam = buildQueryParamsString(query, 1);

        setInternalPromoParam(promoParam);
    }, [query.q]);

    // All tracking handled in one effect
    useEffect(() => {
        if (!searchResults?.analyticsTaxonomy?.page) {
            return;
        }
        const {
            analyticsTaxonomy: analyticsTaxonomyFromResponse
        } = searchResults;

        const analyticsTaxonomy = {
            ...analyticsTaxonomyFromResponse,
            location: analyticsTaxonomyFromResponse.areaName
        };

        let featuredImpressions: ProductTrackingObject[] = [];

        if (!isWidthLessThanBreakpoint && featuredProperties.length != 0)
            featuredImpressions = featuredProperties.map((item, index) =>
                getProductImpression(item, index + 1)
            );

        const analyticsEcommerce = {
            currencyCode: searchResults.analyticsEcommerce.currencyCode,
            impressions: [
                ...(searchResults.analyticsEcommerce.impressions ?? []),
                ...featuredImpressions
            ]
        };

        trackPage(
            analyticsTaxonomy.page,
            analyticsTaxonomy,
            analyticsEcommerce
        );
        segmentSearchEvent(
            analyticsTaxonomy,
            currentSearchSource,
            searchResults.seoBlurb.category,
            filters
        );
    }, [searchResults]);

    useEffect(() => {
        identifyUser(window);
    }, []);

    useEffect(() => {
        const uri = searchResults?.metaInfo?.canonicalUri || '';
        const newCanonicalPath = buildCanonicalPath(uri);

        setCanonicalPath(newCanonicalPath);
    }, [searchResults?.metaInfo?.canonicalUri]);

    /**
       NOTE:
       This param is for internal ads in featured property list (Map and New Home link)
       Both links start a new search using search param in results page, e.g. "q=london&radius=0"
       Remove if Map & New Home were implemented in app-react and shared the same search context
    */
    const currentPage = typeof query.pn === 'string' ? parseInt(query.pn) : 1;

    const handleChangePage = async (
        onPageChangeEvent: PaginationOnPageChangeEvent
    ) => {
        onPageChangeEvent.nativeEvent.preventDefault();

        if (onPageChangeEvent.targetPage === currentPage) return;

        if (onPageChangeEvent.source === 'next') {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: `pagination_next`
            });
        } else if (onPageChangeEvent.source === 'previous') {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: `pagination_back`
            });
        } else {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: `page_${onPageChangeEvent.targetPage}`
            });
        }

        await updateResults(
            filters,
            router,
            searchType,
            currentSearchSource,
            currentSortOrder,
            userAlertId,
            false,
            false,
            onPageChangeEvent.targetPage
        );
    };

    const pageNumberToHref = (pageNumber: number): string | undefined => {
        const currentPath = router.asPath.replace('&view_type=list', '');

        if (currentPage === pageNumber) return undefined;

        return updatePathPageNumber(currentPath, pageNumber);
    };

    const onResetFilters = async (evt: MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();

        const SEARCH_SOURCE = 'facets';
        reduxDispatch(
            resetResultsAsync(
                query,
                router,
                searchType,
                SEARCH_SOURCE,
                currentSortOrder,
                userAlertId
            )
        );
    };

    const sortOrderDropdown = (
        <SortOrderDropdown
            excludeRadiusFromResults={extendedListings.length > 0}
            userAlertId={userAlertId}
            searchType={searchType}
            dispatch={dispatch}
        />
    );

    const authModal = (
        <AuthModal
            isShowing={isAuthModalShowing}
            initialMode={authMode}
            close={closeAuthModal}
            actionAfterAuth={actionAfterAuth}
            resetActionAfterAuth={resetActionAfterAuth}
            heading={authHeader}
        />
    );

    if (loading) {
        if (process.browser) {
            window.scrollTo(0, 0);
        }

        return (
            <h2
                data-testid="loading-search-results"
                style={{ textAlign: 'center' }}
            >
                Loading...
            </h2>
        );
    }

    const handleAuthPrompt = (mode: AuthModalMode) => {
        setAuthMode(mode);
        setAuthHeader('Get more from Zoopla');
        dispatch({
            type: 'SET_ACTION_AFTER_AUTH',
            payload: {
                action: () => null,
                eventLabel: 'lsrp_sign_in_get_instant_alerts_prompt',
                segment: {
                    page: searchResults?.analyticsTaxonomy
                        ?.page as BaseTaxonomy['page'],
                    section: searchResults?.analyticsTaxonomy
                        ?.section as BaseTaxonomy['section'],
                    activity: searchResults?.analyticsTaxonomy?.activity
                }
            }
        });
        openAuthModal(null);
    };

    return (
        <>
            <Head
                pageTitle={searchResults?.metaInfo?.title || ''}
                pageDescription={searchResults?.metaInfo?.description || ''}
                canonicalPath={canonicalPath}
                renderSocialSharingTags={true}
                index={() =>
                    shouldIndexPath(
                        asPath,
                        query,
                        searchResults?.seoBlurb?.category || '',
                        pageNumber,
                        regularListings.length,
                        extendedListings.length
                    )
                }
                schema={() =>
                    getJSONLD(
                        asPath,
                        searchResults?.metaInfo?.canonicalUri || '',
                        breadcrumbs || []
                    )
                }
            />
            <SearchResultsHeader
                breadcrumbs={breadcrumbs || []}
                previousSavedSearchUri={previousSavedSearchUri}
                savedSearchAndAlertsUri={savedSearchAndAlerts?.uri}
                userAlertId={userAlertId}
                isEditSavedSearchBannerVisible={isEditSavedSearchBannerVisible}
                pageTitle={searchResults?.title || ''}
                mapIconLink={links?.mapView || ''}
                isLoggedIn={isLoggedIn}
                sortOrderDropdown={sortOrderDropdown}
            />
            <SearchResultsLayoutGroup>
                <FeaturedPropertyList
                    properties={featuredProperties}
                    sectionType={transactionType}
                    propertyType={seoAccordions[0]?.propertyType}
                    identifier={identifierSelectedValue as string}
                    searchParam={internalPromoParam}
                />

                <Listings
                    searchType={searchType}
                    analyticsTaxonomyIdentifier={
                        searchResults?.analyticsTaxonomy?.searchIdentifier || ''
                    }
                    analyticsTaxonomy={searchResults?.analyticsTaxonomy}
                    analyticsEcommerce={searchResults?.analyticsEcommerce}
                    searchCategory={searchResults?.seoBlurb?.category ?? ''}
                    filters={filters}
                    currentSearchSource={currentSearchSource}
                    error={error}
                    regularListings={regularListings}
                    extendedListings={extendedListings}
                    onResetFilters={onResetFilters}
                    favouritesListings={favourites.listings}
                    authenticateSaveToggle={authenticateSaveToggle}
                    authModal={authModal}
                    pageNumber={pageNumber}
                />

                {regularListings.length > 0 && (
                    <PaginationContainer data-testid="pagination">
                        <StyledPagination
                            previousLabel="< Back"
                            nextLabel="Next >"
                            totalPages={totalPages}
                            currentPage={currentPage}
                            pageNumberToHref={pageNumberToHref}
                            onPageChange={handleChangePage}
                        />
                    </PaginationContainer>
                )}

                <EmailAgents
                    path={path}
                    searchType={searchType}
                    location={filters?.location?.value}
                    isUserLoggedIn={isLoggedIn}
                    createAlert={links?.createAlert || ''}
                />

                <BreadcrumbsShowMobileWrapper>
                    <Breadcrumbs
                        data-testid="mobile-breadcrumbs"
                        data={breadcrumbs || []}
                    />
                </BreadcrumbsShowMobileWrapper>

                <DiscoverMore
                    findAgentsUri={discoverMore?.findAgentsUri || ''}
                    housePricesUri={discoverMore?.housePricesUri || ''}
                    searchArea={
                        searchResults?.analyticsTaxonomy?.areaName || ''
                    }
                />

                <BottomPageAdWrapper>
                    <AdvertSlotDfp
                        showOnly={['>', breakpoints.lsrpSidebar]}
                        position={5}
                        sizes={[
                            [320, 50],
                            [728, 90],
                            [468, 60],
                            [970, 90]
                        ]}
                        adUnitCode={[
                            AD_UNIT_ROOTS.FOOTER,
                            getPageSectionForAdUnit(PAGE_MAPPING)
                        ]}
                    />
                </BottomPageAdWrapper>
            </SearchResultsLayoutGroup>
            <AuthPrompts
                promptType="lsrpSavePropertyDelayPrompt"
                signIn={() => handleAuthPrompt('sign-in')}
                register={() => handleAuthPrompt('register')}
                journey="lsrp_sign_in_get_instant_alerts_prompt"
            />
        </>
    );
};

export default withOptimizely(SearchResults);
